import { Type, setCookies, write } from '@acorns/web-utils'
import { ExecutionResult } from 'graphql'
import { object, string } from 'yup'

import { ChallengeAuthenticateMutation } from 'generated/graphql'
import { hasUdid } from 'utils/udid-utils'

import { isWhitelisted } from '../../utils/login-utils'
import {
  ConfirmButtonTapped,
  ExpiredCodeModalViewed,
  InvalidCodeModalViewed,
  TrustThisDeviceValue,
} from '../../utils/segment'

export type Values = {
  challengeAnswer: string
  challengeId: string
  trustThisDevice: boolean
}

export const validationSchema = object().shape({
  challengeAnswer: string()
    .required()
    .matches(/^[0-9]+$/),
})

// Used by Formik to set initial form values
export const mapPropsToValues = (props): Values => {
  const { challengeAnswer = '', challengeId = '' } = props
  return {
    challengeAnswer,
    challengeId,
    trustThisDevice: false,
  }
}

export const resolveRedirect = (
  defaultRedirect: string,
  referrer?: string,
  preservedRedirect?: string,
): string => {
  let redirect = defaultRedirect
  if (
    referrer &&
    !referrer.includes(window.location.host) &&
    isWhitelisted(referrer)
  ) {
    redirect = referrer
  } else if (preservedRedirect) {
    redirect = `${redirect}${preservedRedirect}`
  } else {
    const { search } = window.location
    redirect = `${redirect}`
    if (search) {
      redirect = `${redirect}${search}`
    }
  }
  return redirect
}

export const handleFormSubmit = (values, { props, setSubmitting }: any) => {
  setSubmitting(true)
  props.setIsLoading(true)
  props.setTitle('Verifying code')
  const rememberMe = values.trustThisDevice
  const challengeAnswer = values.challengeAnswer.toString()
  let challengeId = props.location.data.challengeId
  // this is to check for resendCode option
  if (props.resendChallengeId !== '') {
    challengeId = props.resendChallengeId
  }

  const redirect = resolveRedirect(
    props.env.get('defaultRedirect'),
    document.referrer,
    props.location.data.preservedRedirect,
  )

  return props
    .login({
      variables: {
        input: { ...props.location.data.values, rememberMe },
        challengeAnswerInput: {
          challengeId,
          challengeAnswer,
        },
        oauth: props.isOAuth ?? false,
      },
    })
    .then((result: ExecutionResult<ChallengeAuthenticateMutation>) => {
      if (result.data.authenticate.__typename === 'AuthSession') {
        props.analytics.track(
          'Button Tapped',
          ConfirmButtonTapped(redirect, 'Confirm'),
        )

        if (props.trustThisDeviceWebIsEnabled && hasUdid()) {
          // only track if the checkbox was shown; this is the same visiblity logic to display the checkbox input
          props.analytics.track(
            `Trust This Device ${rememberMe ? 'Accepted' : 'Declined'}`,
            TrustThisDeviceValue('Trust This Device'),
          )
        }

        if (props.isOAuth) {
          write(Type.identityId, result.data.authenticate.identityId, {
            expires: new Date(new Date().getTime() + 2 * 60 * 1000), // expires two minutes from now
          })
        } else {
          setCookies(result.data.authenticate)
        }

        setTimeout(() => {
          props.setTitle('Success')
          props.setShouldRedirect(true)
        }, 500)
      } else if (
        result.data.authenticate.__typename === 'AuthChallengeExpiredException'
      ) {
        props.setIsExpiredModalOpen(true)
        props.analytics.track('Container Viewed', ExpiredCodeModalViewed)
      } else {
        props.setIsModalOpen(true)
        props.analytics.track('Container Viewed', InvalidCodeModalViewed)
      }
    })
    .finally(() => {
      props.setIsLoading(false)
    })
}
